import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { PageNotificationToast } from '../notifications/PageNotificationToast';
import { NotificationBellIcon } from '../notifications/NotificationBellIcon';
import { useDemNotifications } from '../../contexts/DemNotificationsContext';
import { useMaxWidth } from '../../hooks/useMaxWidth';

type PanelPos = {
  top: number;
  maxHeight: number;
};

type AbovePanelPos = {
  left: number;
  bottom: number;
  width: number;
  maxHeight: number;
};

function panelMaxHeight(anchorBottom: number): number {
  const dockGap = 100;
  const reserved = dockGap + 16;
  return Math.max(180, Math.min(420, window.innerHeight - anchorBottom - reserved));
}

function formatTime(iso: string): string {
  return iso.slice(0, 16).replace('T', ' ');
}

type DemNotificationsSlotProps = {
  /** Open the panel above the bell (e.g. sidebar footer). Default: below. */
  placement?: 'below' | 'above';
};

export function DemNotificationsSlot({ placement = 'below' }: DemNotificationsSlotProps) {
  const {
    items,
    persisted,
    unreadPersistedCount,
    activeToast,
    dismissToast,
    clearAll,
    markPersistedRead,
  } = useDemNotifications();
  const navigate = useNavigate();
  const isMobile = useMaxWidth(960);
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState<PanelPos>({ top: 56, maxHeight: 320 });
  const [abovePos, setAbovePos] = useState<AbovePanelPos>({
    left: 12,
    bottom: 80,
    width: 320,
    maxHeight: 360,
  });
  const rootRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const portalAbove = placement === 'above' && !isMobile;

  const badgeCount = items.length + unreadPersistedCount;
  const hasAny = items.length > 0 || persisted.length > 0;

  const merged = useMemo(() => {
    type Row =
      | { kind: 'session'; id: string; title: string; lines: string[]; createdAt: string; unread: boolean }
      | {
          kind: 'persisted';
          id: string;
          title: string;
          lines: string[];
          createdAt: string;
          unread: boolean;
          link: string | null;
        };
    const sessionRows: Row[] = items.map((item) => ({
      kind: 'session',
      id: `s-${item.id}`,
      title: item.title,
      lines: item.lines,
      createdAt: item.createdAt,
      unread: true,
    }));
    const persistedRows: Row[] = persisted.map((row) => ({
      kind: 'persisted',
      id: row.id,
      title: row.title,
      lines: row.body ? [row.body] : [],
      createdAt: row.created_at,
      unread: !row.read_at,
      link: row.link,
    }));
    return [...sessionRows, ...persistedRows].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [items, persisted]);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;

    const update = () => {
      const rect = btnRef.current!.getBoundingClientRect();
      if (portalAbove) {
        const width = Math.min(320, window.innerWidth - 24);
        let left = rect.right - width;
        if (left < 12) left = 12;
        if (left + width > window.innerWidth - 12) {
          left = Math.max(12, window.innerWidth - width - 12);
        }
        const maxHeight = Math.max(160, Math.min(360, rect.top - 24));
        setAbovePos({
          left,
          bottom: window.innerHeight - rect.top + 10,
          width,
          maxHeight,
        });
        return;
      }
      if (!isMobile) return;
      const top = rect.bottom + 8;
      setPanelPos({ top, maxHeight: panelMaxHeight(top) });
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, isMobile, portalAbove]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const panel = open ? (
    <div
      ref={panelRef}
      className={[
        'notifications-bell__panel',
        isMobile ? 'notifications-bell__panel--mobile' : '',
        portalAbove ? 'notifications-bell__panel--above notifications-bell__panel--fixed-above' : '',
        !isMobile && placement === 'above' && !portalAbove ? 'notifications-bell__panel--above' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="menu"
      style={
        portalAbove
          ? {
              position: 'fixed',
              left: abovePos.left,
              bottom: abovePos.bottom,
              top: 'auto',
              right: 'auto',
              width: abovePos.width,
              maxWidth: abovePos.width,
              maxHeight: abovePos.maxHeight,
            }
          : isMobile
            ? {
                top: panelPos.top,
                maxHeight: panelPos.maxHeight,
              }
            : undefined
      }
    >
      <div className="notifications-bell__panel-head">
        <strong>Notifications</strong>
        {hasAny ? (
          <button type="button" className="account-side-btn account-side-btn--muted" onClick={() => clearAll()}>
            Clear all
          </button>
        ) : null}
      </div>

      {!hasAny ? (
        <p className="account-side-muted notifications-bell__empty">No notifications yet.</p>
      ) : (
        <ul className="notifications-bell__list">
          {merged.map((item) => (
            <li
              key={item.id}
              className={`notifications-bell__item${item.unread ? ' notifications-bell__item--unread' : ''}`}
            >
              <button
                type="button"
                className={`notifications-bell__item-body${
                  item.kind === 'persisted' && item.link ? '' : ' notifications-bell__item-body--static'
                }`}
                onClick={() => {
                  if (item.kind !== 'persisted') return;
                  void markPersistedRead(item.id);
                  if (item.link) {
                    setOpen(false);
                    navigate(item.link);
                  }
                }}
              >
                <strong>{item.title}</strong>
                {item.lines.map((line, idx) => (
                  <span key={`${item.id}-line-${idx}`}>{line}</span>
                ))}
                <time className="notifications-bell__time">{formatTime(item.createdAt)}</time>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  ) : null;

  return (
    <>
      <div className="notifications-bell" ref={rootRef}>
        <button
          ref={btnRef}
          type="button"
          className="btn-secondary btn-sm notifications-bell__btn"
          aria-expanded={open}
          aria-label={badgeCount > 0 ? `Notifications (${badgeCount})` : 'Notifications'}
          onClick={() => setOpen((value) => !value)}
        >
          <span className="notifications-bell__icon" aria-hidden>
            <NotificationBellIcon />
          </span>
          {badgeCount > 0 ? (
            <span className="notifications-bell__badge">{badgeCount > 99 ? '99+' : badgeCount}</span>
          ) : null}
        </button>
        {(isMobile || portalAbove) && panel ? createPortal(panel, document.body) : panel}
      </div>
      {activeToast
        ? createPortal(
            <PageNotificationToast notification={activeToast} onDismiss={dismissToast} />,
            document.body,
          )
        : null}
    </>
  );
}
